import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useMemo, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useLocalizeMessage, Table, Link } from 'libs.nucleus';
import { DataReportsContext } from '../../../contexts/data_reports/data_reports.context';
const useStableData = (data) => {
    const ref = useRef(data);
    // isNewInfra resolves asynchronously (after the batched Library lookup completes) without any
    // row's id/key changing, so it must be part of the fingerprint — otherwise this snapshot never
    // updates to reflect it once the lookup finishes.
    const fingerprint = data.map((r) => `${r.id ?? r.key}:${r.isNewInfra}`).join(',');
    const prevFingerprint = useRef(fingerprint);
    if (prevFingerprint.current !== fingerprint) {
        ref.current = data;
        prevFingerprint.current = fingerprint;
    }
    return ref.current;
};
export const ReportsTable = ({ studyId, data: rawData = [], isLoading, customHeaderContent, reportBasePath, }) => {
    const translate = useLocalizeMessage();
    const { activeReportsTab } = useContext(DataReportsContext);
    const data = useStableData(rawData);
    const columns = useMemo(() => {
        const reportEnv = activeReportsTab === 0 ? 'test' : 'prod';
        const basePath = reportBasePath || `/studies/${studyId}/data/reports`;
        return [
            {
                header: translate('Name'),
                accessorKey: 'data.name',
                cell: ({ row }) => {
                    const studyConfigId = row.original.studyConfigId;
                    const isNewInfra = row.original.isNewInfra;
                    const linkTo = studyConfigId
                        ? `${basePath}/${row.original.key}?env=${reportEnv}&rid=${studyConfigId}`
                        : `${basePath}/${row.original.key}?env=${reportEnv}`;
                    // Reports without a confirmed new-infra (starrocks) cube config behind them render as
                    // plain text — following the link would 404/network-error rather than load a dashboard.
                    if (!isNewInfra) {
                        return _jsx("div", { children: row.original.data.name });
                    }
                    return (_jsx("div", { children: _jsx(Link, { LinkComponent: RouterLink, to: linkTo, children: row.original.data.name }) }));
                },
            },
            {
                header: translate('Description'),
                accessorKey: 'data.description',
            },
        ];
    }, [activeReportsTab, reportBasePath, studyId]);
    return (_jsx(Table, { columns: columns, data: data, hasPagination: true, initialPageSize: 10, isLoading: isLoading, paginationNavigationScreenReaderLabel: translate('Reports table navigation'), useInternalSearch: true, customHeaderContent: customHeaderContent }));
};
